/**
 * Single-line stage cell with paired diagnosis overflow details.
 */

import { useRef, useState } from "react";
import ToolTipUsingPortal from "../../../toolTipUsingPortal/toolTipUsingPortal";
import { splitDiagnosisDescriptions } from "./DiagnosisWithOverflowCell";

/** Raw stage and diagnosis strings used by the stage overflow cell. */
export interface StageWithOverflowValue {
  stage: string;
  diagnosis: string;
}

/** One diagnosis aligned with its stage. */
export interface DiagnosisStagePair {
  diagnosis: string;
  stage: string;
}

/** Props accepted by the stage overflow table cell. */
interface StageWithOverflowCellProps {
  value: StageWithOverflowValue;
}

/**
 * Pair comma-separated stages with their diagnosis descriptions.
 *
 * @param diagnosis - API diagnosis description string
 * @param stage - API comma-separated stage string
 * @returns Ordered diagnosis/stage pairs
 */
export function pairDiagnosesWithStages(
  diagnosis: string,
  stage: string,
): DiagnosisStagePair[] {
  const stages = stage
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
  const diagnoses = splitDiagnosisDescriptions(diagnosis);

  return stages.map((stageValue, index) => ({
    diagnosis:
      diagnoses[index] ||
      (index === 0 ? diagnosis.trim() : "Diagnosis unavailable"),
    stage: stageValue,
  }));
}

/**
 * Render the first stage and reveal paired diagnosis/stage details on hover.
 *
 * @param props - Raw stage and diagnosis strings
 * @returns Single-line stage cell plus a portaled details panel
 */
export function StageWithOverflowCell({ value }: StageWithOverflowCellProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const pairs = pairDiagnosesWithStages(
    value?.diagnosis ?? "",
    value?.stage ?? "",
  );
  const firstPair = pairs[0];
  const overflowCount = Math.max(0, pairs.length - 1);

  return (
    <div
      ref={anchorRef}
      className="flex h-full w-full min-w-0 items-center overflow-hidden"
      onMouseEnter={() => setShowTooltip(pairs.length > 1)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span className="truncate">{firstPair?.stage || "—"}</span>
      {overflowCount > 0 && <span className="shrink-0"> +{overflowCount}</span>}
      <ToolTipUsingPortal
        placement="bottom-left"
        showTooltip={showTooltip}
        parentRef={anchorRef}
        wrapText
        maxWidth="520px"
      >
        <div className="overflow-hidden rounded-lg border border-primaryGray-14 bg-white">
          <div className="grid grid-cols-[1fr_auto] gap-6 border-b border-primaryGray-14 bg-primaryGray-16 px-5 py-3 text-small font-semibold uppercase text-primaryGray-5">
            <span>Diagnosis</span>
            <span>Stage</span>
          </div>
          {pairs.map((pair, index) => (
            <div
              key={`${pair.stage}-${index}`}
              className="grid grid-cols-[1fr_auto] gap-6 border-b border-primaryGray-14 px-5 py-3 text-small text-primaryGray-2 last:border-b-0"
            >
              <span>{pair.diagnosis || "—"}</span>
              <span className="whitespace-nowrap">{pair.stage}</span>
            </div>
          ))}
        </div>
      </ToolTipUsingPortal>
    </div>
  );
}

export default StageWithOverflowCell;
